import { projectLap, type Origin, type Point } from "./geo";
import type { AlignedLap } from "./trace";

/**
 * The road itself, rebuilt from what the recording says about it, with every lap
 * placed on it by how far it ran from the middle.
 *
 * Two channels make this possible. Path Lateral is how far the car is from the
 * circuit's own centre line, positive to the right; Track Edge is how far the edge on
 * that same side is from the centre line. So every sample says where the middle of the
 * road was — the car's position less its offset — and where one of its edges was.
 * Rebuilt from ten laps of Laguna Seca, the centre lines agree to 0.7 m; with the
 * opposite sign they sit 1.4 m apart.
 *
 * Laps are drawn from that shared centre line and their own offset rather than from
 * their raw GPS. Distance on the grid is distance along the centre line, so each lap's
 * offset at a grid point is exactly where it sat across the road there: a lap inside
 * the limits is drawn inside them, and one that ran wide is drawn wide by as much as
 * it did.
 */
export interface TrackModel {
  left: Array<Point | null>;
  right: Array<Point | null>;
  /** Each lap's line, keyed like the lap. */
  lines: Map<string, Array<Point | null>>;
}

/**
 * Every line and both edges hang off the centre line's normal, so noise in its
 * direction is multiplied by however far from the middle a car was, and shows up as a
 * wobble in each line. Smoothing the centre line pulls it towards the inside of a
 * corner, but it moves the edges and every line together, so where a car sat across
 * the road is untouched.
 */
const TANGENT_HALF_SPAN_M = 5;
const CENTRE_SMOOTHING_M = 6;
/** LMU's edge widths change in steps between track segments, which reads as jagged. */
const EDGE_SMOOTHING_M = 8;
/**
 * Path Lateral is recorded at 10 Hz, a sample every 3 to 6 m at racing speed, so on a
 * one-metre grid it is a staircase that draws each line with a row of teeth. A car
 * does not move sideways in steps, and over this span smoothing moves an apex by a few
 * centimetres.
 */
const LATERAL_SMOOTHING_M = 3;
/**
 * Narrower than any racing surface. A one-metre bucket that straddles the car crossing
 * the middle averages the two edges towards zero, and that is not a road.
 */
const MIN_HALF_WIDTH_M = 2.5;

export function buildTrack(
  laps: AlignedLap[],
  origin: Origin,
  distanceStepM: number,
): TrackModel | null {
  const step = distanceStepM > 0 ? distanceStepM : 1;
  const length = laps[0]?.time.length ?? 0;
  const halfSpan = Math.max(1, Math.round(TANGENT_HALF_SPAN_M / step));

  const sumX = new Float64Array(length);
  const sumY = new Float64Array(length);
  const count = new Uint16Array(length);
  const placed: Array<{ lap: AlignedLap; offset: Array<number | null> }> = [];

  const lateralHalf = Math.max(1, Math.round(LATERAL_SMOOTHING_M / step));

  for (const lap of laps) {
    const points = projectLap(lap, origin);
    if (!lap.channels.lateral_offset || !lap.channels.track_edge || !points) continue;
    const offset = smoothNullable(lap.channels.lateral_offset, lateralHalf);

    const normals = rightNormals(points, halfSpan);
    for (let index = 0; index < length; index += 1) {
      const point = points[index];
      const normal = normals[index];
      const lateral = offset[index];
      if (!point || !normal || lateral === null || lateral === undefined) continue;
      sumX[index] += point.x - lateral * normal.x;
      sumY[index] += point.y - lateral * normal.y;
      count[index] += 1;
    }
    placed.push({ lap, offset });
  }

  if (placed.length === 0) return null;

  const centre = smoothPoints(
    Array.from({ length }, (_, index) =>
      count[index] > 0
        ? { x: sumX[index] / count[index], y: sumY[index] / count[index] }
        : null,
    ),
    Math.max(1, Math.round(CENTRE_SMOOTHING_M / step)),
  );
  const normals = rightNormals(centre, halfSpan);

  const smoothing = Math.max(1, Math.round(EDGE_SMOOTHING_M / step));
  const placedLaps = placed.map((entry) => entry.lap);
  const rightWidth = sideWidths(placedLaps, length, 1, smoothing);
  const leftWidth = sideWidths(placedLaps, length, -1, smoothing);
  if (!rightWidth && !leftWidth) return null;

  // A side no lap ever went to can only be guessed, and the other side is the best
  // guess there is: the centre line is roughly central, to within a couple of metres.
  const right = (rightWidth ?? leftWidth) as Float64Array;
  const left = (leftWidth ?? rightWidth) as Float64Array;

  const along = (width: Float64Array | null, sign: number) =>
    centre.map((point, index) => {
      const normal = normals[index];
      if (!point || !normal || !width) return null;
      return {
        x: point.x + sign * width[index] * normal.x,
        y: point.y + sign * width[index] * normal.y,
      };
    });

  const lines = new Map<string, Array<Point | null>>();
  for (const { lap, offset } of placed) {
    lines.set(
      lap.key,
      centre.map((point, index) => {
        const normal = normals[index];
        const lateral = offset[index];
        if (!point || !normal || lateral === null || lateral === undefined) return null;
        return { x: point.x + lateral * normal.x, y: point.y + lateral * normal.y };
      }),
    );
  }

  return { left: along(left, -1), right: along(right, 1), lines };
}

/**
 * How far one edge is from the centre line at every grid point.
 *
 * A lap only ever reports the edge on its own side of the road, so where none of the
 * laps went to this side the width is carried along the track from where one did.
 * Measured by hiding each lap's reading from the others, that lands within 4 cm of the
 * true edge half the time and within a metre nine times in ten — where assuming the
 * road is symmetric about the centre line is out by 0.8 m and 2 m.
 */
function sideWidths(
  laps: AlignedLap[],
  length: number,
  side: 1 | -1,
  smoothing: number,
): Float64Array | null {
  const observed = new Array<number | null>(length).fill(null);
  const samples: number[] = [];

  for (let index = 0; index < length; index += 1) {
    samples.length = 0;
    for (const lap of laps) {
      const edge = lap.channels.track_edge?.[index];
      if (edge === null || edge === undefined) continue;
      const width = edge * side;
      if (width >= MIN_HALF_WIDTH_M) samples.push(width);
    }
    if (samples.length > 0) observed[index] = median(samples);
  }

  const first = observed.findIndex((value) => value !== null);
  if (first < 0) return null;

  const filled = new Float64Array(length);
  let previous = first;
  for (let index = 0; index < length; index += 1) {
    const value = observed[index];
    if (value !== null) {
      const from = observed[previous] as number;
      for (let gap = previous + 1; gap < index; gap += 1) {
        filled[gap] = from + ((value - from) * (gap - previous)) / (index - previous);
      }
      filled[index] = value;
      previous = index;
    } else if (index < first) {
      filled[index] = observed[first] as number;
    }
  }
  for (let index = previous + 1; index < length; index += 1) {
    filled[index] = observed[previous] as number;
  }

  return smoothValues(filled, smoothing);
}

/** Unit vectors pointing to the right of the direction of travel, y down. */
function rightNormals(points: Array<Point | null>, halfSpan: number): Array<Point | null> {
  return points.map((_, index) => {
    const from = points[Math.max(0, index - halfSpan)];
    const to = points[Math.min(points.length - 1, index + halfSpan)];
    if (!from || !to) return null;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);
    if (length < 1e-6) return null;
    return { x: -dy / length, y: dx / length };
  });
}

function smoothPoints(points: Array<Point | null>, half: number): Array<Point | null> {
  return points.map((point, index) => {
    if (!point) return null;
    let x = 0;
    let y = 0;
    let count = 0;
    for (
      let other = Math.max(0, index - half);
      other <= Math.min(points.length - 1, index + half);
      other += 1
    ) {
      const neighbour = points[other];
      if (!neighbour) continue;
      x += neighbour.x;
      y += neighbour.y;
      count += 1;
    }
    return { x: x / count, y: y / count };
  });
}

function smoothNullable(values: Array<number | null>, half: number): Array<number | null> {
  return values.map((value, index) => {
    if (value === null) return null;
    let total = 0;
    let count = 0;
    for (
      let other = Math.max(0, index - half);
      other <= Math.min(values.length - 1, index + half);
      other += 1
    ) {
      const neighbour = values[other];
      if (neighbour === null) continue;
      total += neighbour;
      count += 1;
    }
    return total / count;
  });
}

function smoothValues(values: Float64Array, half: number): Float64Array {
  const out = new Float64Array(values.length);
  for (let index = 0; index < values.length; index += 1) {
    const from = Math.max(0, index - half);
    const to = Math.min(values.length - 1, index + half);
    let total = 0;
    for (let other = from; other <= to; other += 1) total += values[other];
    out[index] = total / (to - from + 1);
  }
  return out;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}
